import React, { useContext } from "react";
import { Alert, Box, Grid, Paper, Typography } from "@mui/material";
import moment from "moment";
import { Link as RouterLink } from "react-router-dom";
import ItemProgressTracker from "../../order/component/ItemProgressTracker";
import CustomerContactInfoButton from "../../common/component/CustomerContactInfoButton";
import HTMLViewer from "../../common/component/HTMLViewer";
import LineItemDetails from "../../common/component/lineitem/LineItemDetails";
import ImageCarousel from "../../common/component/ImageCarousel";
import { IMG_PLACEHOLDER, STORAGE_BUCKET } from "../../common/constants";
import { getItemProgress } from "../../common/util";
import { UserContext } from "../../context/UserContext";

const metaOverlineStyle = {
  display: "flex",
  alignItems: "center",
  fontSize: ".8rem",
  color: "#777",
  gap: "4px",
};

export function KitchenOrderMetaRow(props) {
  const { order, trailing } = props;

  return (
    <Box sx={{ display: "flex", gap: "5px", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
      <CustomerContactInfoButton customer={order.customer} />
      <Typography variant="overline" style={metaOverlineStyle}>
        <span style={{ color: "#CCC" }}>{` | `}</span>
        {moment(order.fulfillment.date).format("ddd, MMM D")} [{order.fulfillment.time}]
      </Typography>
      <Typography variant="overline" style={metaOverlineStyle}>
        <span style={{ color: "#CCC" }}>{` ● `}</span>
        {order.fulfillment.type}
      </Typography>
      {order.fulfillment.type === "pickup" && (
        <Typography variant="overline" style={metaOverlineStyle}>
          <span style={{ color: "#CCC" }}>{` ● `}</span>
          {order.fulfillment.location
            ? order.fulfillment.location.street
            : order.fulfillment.address.split(",")[0]}
        </Typography>
      )}
      {trailing}
    </Box>
  );
}

export default function KitchenOrder(props) {
  const {
    order,
    focusIndex,
    onItemProgressChange,
    onFocusItemChange,
    basePath = "/production/tracker/order",
    hideOrderMeta,
    hideStatusAlert,
  } = props;
  const { user } = useContext(UserContext);
  const itemPath = (idx) => `${basePath}/${order.id}/item/${idx}`;

  const focusItem = Number.isInteger(focusIndex) ? order.items[focusIndex] : undefined;
  const otherItems = order.items
    .map((item, idx) => ({ item, idx }))
    .filter(({ idx }) => idx !== focusIndex);

  const renderItem = (item, idx) => {
    return (
      <Box key={idx} my={2}>
        <Grid container spacing={2}>
          <Grid item xs="auto">
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 40 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  border: "1px solid #DDD",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="h6">{item.quantity}</Typography>
              </Box>
              <Typography variant="overline" sx={{ lineHeight: 1.2, mt: 0.25 }}>
                QTY
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <ImageCarousel images={item.images} />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              {item.title}
            </Typography>
            <LineItemDetails item={item} />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderItemCard = (item, idx) => (
    <Paper key={idx} elevation={0} sx={{ mb: 2, bgcolor: "background.paper" }}>
      <Box px={2} py={1}>
        {renderItem(item, idx)}
      </Box>
    </Paper>
  );

  const renderProgressTracker = (item, idx) => {
    const progress = getItemProgress(order, item, user?.config?.kds?.progressSteps);
    return (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          PROGRESS TRACKER
        </Typography>
        <Paper elevation={0} sx={{ bgcolor: "background.paper" }}>
          <Box px={2} py={2}>
            <ItemProgressTracker
              key={progress.step}
              orderId={order.id}
              item={item}
              itemIndex={idx}
              progress={progress}
              onChange={onItemProgressChange}
            />
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderItemSummary = (item, idx) => {
    const image = item.images?.find(Boolean) || `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`;
    const details = item.variant?.size?.name || "";

    const summaryCardSx = {
      mb: 1,
      bgcolor: "background.paper",
      display: "block",
      width: "100%",
      textAlign: "left",
      color: "inherit",
      textDecoration: "none",
      cursor: "pointer",
      border: "none",
      font: "inherit",
    };

    const cardProps = onFocusItemChange
      ? { component: "button", type: "button", onClick: () => onFocusItemChange(idx) }
      : { component: RouterLink, to: itemPath(idx) };

    return (
      <Paper key={idx} elevation={0} sx={summaryCardSx} {...cardProps}>
        <Box px={2} py={1.5} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <img
            alt=""
            src={image}
            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap>
              {`${item.quantity || 1}x ${item.title || item.type}`}
            </Typography>
            {details && (
              <Typography variant="body2" color="textSecondary" noWrap>
                {details}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  let alert = "";
  if (order.status === "fulfilled" || order.status === "completed") {
    alert = <Alert severity="success">This order has been completed</Alert>;
  } else if (order.status === "canceled") {
    alert = <Alert severity="error">This order has been canceled</Alert>;
  }

  const addOns = order.invoice
    ? Object.entries(order.invoice).filter(([k, v]) => v.status !== "void")
    : [];

  const renderInstructions = () =>
    order.kitchen ? (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          INSTRUCTIONS
        </Typography>
        <Paper elevation={0} sx={{ bgcolor: "background.paper" }}>
          <Box px={2} py={2}>
            <HTMLViewer content={order.kitchen.notes} style={{ fontSize: ".85rem" }} />
          </Box>
        </Paper>
      </Box>
    ) : null;

  const renderAddOns = () =>
    addOns.length > 0 ? (
      <Box mt={3} mb={2}>
        <Typography variant="overline" color="textSecondary">
          ADD-ON
        </Typography>
        <Paper elevation={0} sx={{ bgcolor: "background.paper" }}>
          <Box px={2} py={2}>
            {addOns.map(([key, value]) => (
              <Box key={key} mb={addOns.length > 1 ? 1 : 0}>
                <Typography variant="body2">{value.description}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    ) : null;

  return (
    <Box my={hideOrderMeta ? 0 : 6}>
      {!hideOrderMeta && (
        <Grid container alignItems={"center"}>
          <Grid item xs={12}>
            <KitchenOrderMetaRow order={order} />
          </Grid>
        </Grid>
      )}
      {!hideStatusAlert && alert && (
        <Box mb={2} mt={hideOrderMeta ? 0 : undefined}>
          {alert}
        </Box>
      )}
      {focusItem ? (
        <>
          {renderItemCard(focusItem, focusIndex)}
          {renderInstructions()}
          {renderAddOns()}
          {renderProgressTracker(focusItem, focusIndex)}
        </>
      ) : (
        <Box mb={2}>
          {order.items.map((item, idx) => renderItemSummary(item, idx))}
          {renderInstructions()}
          {renderAddOns()}
        </Box>
      )}
      {focusItem && otherItems.length > 0 && (
        <Box mt={3}>
          <Typography variant="overline" color="textSecondary">
            Other items in this order
          </Typography>
          {otherItems.map(({ item, idx }) => renderItemSummary(item, idx))}
        </Box>
      )}
    </Box>
  );
}
